import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PortalCashTransactionsLedger,
  downloadBlob,
  type CashTransactionFilterState,
  type CashTransactionListResponse,
  defaultCashTransactionFilters,
} from '@kioskgaming/ui';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import AppLayout from '../../components/layout/AppLayout';
import { getTableSortColumns } from '../../config/tableSortConfig';
import useTableSort from '../../hooks/useTableSort';
import { exportAgentCashTransactions, getAgentCashTransactions } from '../../services/api';

export default function CashTransactionsLedgerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<CashTransactionListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortFetchTick, setSortFetchTick] = useState(0);

  const tableSort = useTableSort({
    columns: getTableSortColumns('agentCashTransactions'),
    onChange: () => setSortFetchTick((t) => t + 1),
  });

  const appliedSort = useMemo(
    () => ({
      sortBy: (data?.pagination?.sortBy ?? null) as string | null,
      sortOrder: (data?.pagination?.sortOrder ?? null) as SortDirection | null,
    }),
    [data?.pagination?.sortBy, data?.pagination?.sortOrder],
  );

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort.sortBy ?? tableSort.sortBy,
        direction: appliedSort.sortOrder ?? tableSort.sortOrder,
      }
    : {
        activeKey: appliedSort.sortBy,
        direction: appliedSort.sortOrder,
      };

  const onFetch = useCallback(
    async ({
      filters,
      page,
      limit,
    }: {
      filters: CashTransactionFilterState;
      page: number;
      limit: number;
    }) => {
      setLoading(true);
      try {
        const res = await getAgentCashTransactions({
          page,
          limit,
          status: filters.status || undefined,
          payment_method: filters.paymentMethod || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          search: filters.search.trim() || undefined,
          ...tableSort.sortParams,
        });
        if (!res.success) throw new Error(res.message || 'Could not load cash transactions');
        setData((res as { data?: CashTransactionListResponse }).data ?? null);
      } catch (e: unknown) {
        setData(null);
        toast.error(e instanceof Error ? e.message : 'Could not load cash transactions');
      } finally {
        setLoading(false);
      }
    },
    [tableSort.sortParams],
  );

  const handleExport = useCallback(async ({ filters }: { filters: CashTransactionFilterState }) => {
    setExporting(true);
    try {
      const { blob, filename } = await exportAgentCashTransactions({
        status: filters.status || undefined,
        payment_method: filters.paymentMethod || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        search: filters.search.trim() || undefined,
      });
      downloadBlob(blob, filename);
      toast.success('Cash transactions exported');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, []);

  return (
    <AppLayout>
      <PortalCashTransactionsLedger
        loading={loading}
        data={data}
        onFetch={onFetch}
        initialFilters={{ ...defaultCashTransactionFilters(), period: 'today' }}
        onExport={handleExport}
        exporting={exporting}
        onViewDetail={(row) =>
          navigate(`/cash-transactions/${row.id}?created_at=${encodeURIComponent(row.createdAt)}`)
        }
        className="border-0 shadow-none rounded-none"
        sortable
        sortActiveKey={headerSort.activeKey}
        sortDirection={headerSort.direction}
        onSort={tableSort.toggleSort}
        sortFetchTick={sortFetchTick}
      />
    </AppLayout>
  );
}
